import classNames from 'classnames';
import isString from 'lodash-es/isString';
import { ForwardedRef, ReactNode, useState, CSSProperties } from 'react';

import useBackgroundIsDark from '../../hooks/useBackgroundIsDark';

import DocumentsList, { DocumentsListProps } from '../lists/DocumentsList';
import Background from '../partials/Background';

import styles from '../../styles/blocks/documents-row-block.module.css';

export interface DocumentsRowBlockProps extends DocumentsListProps {
    title?: ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    background?: string | Background | null;
    count?: number;
    withShadow?: boolean | null;
    withBorderRadius?: boolean;
    withoutMaxWidth?: boolean;
    style?: CSSProperties;
    className?: string | null;
    titleClassName?: string | null;
    innerClassName?: string | null;
    listClassName?: string | null;
    itemsClassName?: string | null;
    backgroundClassName?: string | null;
    onEmpty?: () => void;
    onFilled?: () => void;
    onLoaded?: (loadedItems: FullDocument[]) => void;
}

function DocumentsRowBlock({
    title = null,
    header = null,
    footer = null,
    background = null,
    count = 12,
    withShadow = null,
    withBorderRadius = false,
    withoutMaxWidth = false,
    style = null,
    className = null,
    titleClassName = null,
    innerClassName = null,
    listClassName = null,
    itemsClassName = null,
    backgroundClassName = null,
    onEmpty = null,
    onFilled = null,
    onLoaded = null,
    ref: containerRef = null,
    ...props
}: DocumentsRowBlockProps & { ref?: ForwardedRef<HTMLDivElement> | null }) {
    const finalBackground = isString(background) ? { color: background } : background;

    const backgroundIsDark = useBackgroundIsDark(finalBackground);

    const [items, setItems] = useState<FullDocument[] | null>(null);

    const onListLoaded = (loadedItems: FullDocument[]) => {
        setItems(loadedItems);
        if (loadedItems.length === 0 && onEmpty !== null) {
            onEmpty();
        } else if (loadedItems.length > 0 && onFilled !== null) {
            onFilled();
        }
        if (onLoaded !== null) {
            onLoaded(loadedItems);
        }
    };

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.textIsWhite]: backgroundIsDark,
                    [styles.withBackground]: finalBackground !== null,
                    [styles.withBorderRadius]: withBorderRadius,
                    [styles.withoutMaxWidth]: withoutMaxWidth,
                    [styles.withShadow]: withShadow,
                    [styles.isEmpty]: items !== null && items.length === 0,
                },
                className,
            ])}
            style={style}
            ref={containerRef}
        >
            {finalBackground !== null ? (
                <Background
                    {...finalBackground}
                    className={classNames([styles.background, backgroundClassName])}
                />
            ) : null}
            <div className={classNames([styles.inner, innerClassName])}>
                {header ||
                    (title !== null ? (
                        <div className={classNames([styles.title, titleClassName])}>{title}</div>
                    ) : null)}
                <div className={classNames([styles.list, listClassName])}>
                    <DocumentsList
                        presentation="responsive"
                        cardWidth={150}
                        itemsClassName={classNames([
                            styles.items,
                            {
                                [itemsClassName]: itemsClassName !== null,
                            },
                        ])}
                        cardClassName={styles.card}
                        // cardSize="small"
                        placeholderCount={count}
                        minColumns={2}
                        count={count}
                        onLoaded={onListLoaded}
                        {...props}
                    />
                </div>
                {footer}
            </div>
        </div>
    );
}

export default DocumentsRowBlock;
